import {
    Component,
    OnInit,
    ChangeDetectorRef,
    signal,
    inject,
    computed,
    input,
    output,
    ViewChild,
    ChangeDetectionStrategy,
} from "@angular/core";
import { Subject } from "rxjs";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { BudgetService } from "./budget.service";
import { S25Util } from "../../../util/s25-util";
import { Budget } from "./Budget";
import { OrganizationService } from "../../../services/organization.service";
import { Bind } from "../../../decorators/bind.decorator";
import { CurrencyPipe, DecimalPipe } from "@angular/common";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { Table } from "../../s25-table/Table";
import { GenericTableActionsComponent, TableAction } from "../../s25-table/generics/generic.table.actions.component";
import { S25PricingUtil } from "../../s25-pricing/s25.pricing.util";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { SeSecurityStore } from "../se.security.store";
import { BudgetEditModalComponent } from "./admin/budget.edit.modal.component";
import { WorkflowService } from "../../workflow/service/workflow.service";
import { BudgetSpreadsheetUtil } from "./budget.spreadsheet.util";
import { AlertService } from "../../../services/alert.service";
import { BudgetLabelPhrases, BudgetLabels } from "./budget.labels";

@TypeManagerDecorator("s25-ng-org-budget")
@Component({
    selector: "s25-ng-org-budget",
    template: `
        @if (init()) {
            <div class="budget-dashboard">
                <!-- Header Section -->
                <div class="budget-header">
                    <h2 class="budget-title">{{ labels.BUDGET_DASHBOARD }}</h2>
                </div>

                <!-- Loading State -->
                @if (isLoading()) {
                    <s25-ng-loading-inline-static [text]="labels.LOADING_BUDGET_DATA"></s25-ng-loading-inline-static>
                }

                <!-- Error State -->
                @if (error()) {
                    <div class="error-message">
                        <p>{{ error() }}</p>
                        <s25-ng-button
                            [type]="'outline'"
                            [buttonText]="'Retry'"
                            [onClick]="loadBudgetData"
                        ></s25-ng-button>
                    </div>
                }

                <!-- Budget Summary -->
                @if (!isLoading() && !error()) {
                    <div class="summary-cards">
                        <div class="summary-card">
                            <div class="summary-label">{{ labels.ALLOCATION_ALLOCATION }}</div>
                            <div class="summary-value">
                                {{ budgetSummary?.totalAllocated || 0 | currency: "USD" : "symbol" : "1.0-0" }}
                            </div>
                        </div>
                        <div class="summary-card">
                            <div class="summary-label">Approved Plans</div>
                            <div class="summary-value">
                                {{ budgetSummary?.approvedPlanAllocated || 0 | currency: "USD" : "symbol" : "1.0-0" }}
                            </div>
                            <div class="summary-helper">{{ budgetSummary?.approvedPlansCount || 0 }} approved</div>
                        </div>
                        <div class="summary-card">
                            <div class="summary-label">Pending Plans</div>
                            <div class="summary-value">
                                {{ budgetSummary?.pendingPlanAllocated || 0 | currency: "USD" : "symbol" : "1.0-0" }}
                            </div>
                            <div class="summary-helper">
                                {{ budgetSummary?.pendingPlansCount || 0 }} awaiting review
                            </div>
                        </div>
                        <div class="summary-card">
                            <div class="summary-label">Remaining To Allocate</div>
                            <div class="summary-value">
                                {{ budgetSummary?.remainingBalance || 0 | currency: "USD" : "symbol" : "1.0-0" }}
                            </div>
                            <div class="summary-helper">
                                {{ budgetSummary?.utilizationPercentage || 0 | number: "1.0-1" }}% utilized
                            </div>
                        </div>
                    </div>

                    <!-- Budget Table -->
                    <div class="budgets-table-section">
                        <div class="section-header">
                            <h3>{{ labels.ORGANIZATION_BUDGETS }}</h3>
                            @if (canViewBudget()) {
                                <s25-ng-button
                                    [type]="'outline'"
                                    [buttonText]="'Export to Excel'"
                                    [disabled]="exporting() || !budgets.length"
                                    [isLoading]="exporting()"
                                    [onClick]="exportBudgetCsv"
                                ></s25-ng-button>
                            }
                        </div>

                        <s25-ng-table
                            [dataSource]="budgetTableData"
                            [hasRefresh]="true"
                            [hasFilter]="true"
                            [columnSortable]="true"
                            [hasTotalRowCount]="true"
                            [caption]="labels.ORGANIZATION_BUDGETS"
                            [noResultsMessage]="labelPhrases.noAllocationsForOrg"
                        ></s25-ng-table>
                    </div>
                }

                <s25-ng-budget-edit-modal
                    #budgetEditModal
                    (budgetUpdated)="onBudgetUpdated($event)"
                ></s25-ng-budget-edit-modal>
            </div>
        }
    `,
    styles: `
        .budget-dashboard {
            padding: 1.5rem;
            max-width: 1200px;
            margin: 0 auto;
        }

        .budget-header {
            text-align: center;
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid var(--color--border);
        }

        .budget-title {
            font-size: 1.5rem;
            color: var(--color--text-muted);
            margin: 0 0 0.5rem 0;
        }

        .fiscal-year {
            font-size: 1rem;
            color: var(--color--text-muted);
            font-weight: 500;
            opacity: 0.9;
        }

        .summary-cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 1rem;
            margin-bottom: 1.5rem;
        }

        .summary-card {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 1rem 1.25rem;
            box-shadow: 0 1px 3px var(--color--border);
        }

        .summary-label {
            color: var(--color--text-muted);
            font-size: 0.85rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
            text-transform: uppercase;
            letter-spacing: 0.04em;
        }

        .summary-value {
            color: var(--color--text);
            font-size: 1.4rem;
            font-weight: 700;
            margin-bottom: 0.25rem;
        }

        .summary-helper {
            color: var(--color--text-muted);
            font-size: 0.85rem;
            opacity: 0.92;
        }

        .error-message {
            text-align: center;
            padding: 2rem;
            background: var(--color--surface-bg-error);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            margin: 1rem 0;
        }

        .error-message p {
            color: var(--error-message-color);
            margin-bottom: 1rem;
        }

        .budgets-table-section {
            margin-bottom: 2rem;
            background: var(--color--surface-bg);
            border-radius: 8px;
            box-shadow: 0 1px 3px var(--color--border);
            overflow: hidden;
        }

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.5rem;
            background: var(--color--surface-2-bg);
            border-bottom: 1px solid var(--color--border);
        }

        .section-header h3 {
            margin: 0;
            font-size: 1.2rem;
            color: var(--color--text);
            font-weight: 600;
        }

        /* Budget Table Styles */
        ::ng-deep .budgets-table-section .s25ngTable {
            border: none;
            box-shadow: none;
        }

        ::ng-deep .budgets-table-section .rose-object-table-header {
            margin: 0;
            padding: 1rem 1.5rem;
            background: transparent;
        }

        @media (max-width: 768px) {
            .budget-dashboard {
                padding: 1rem;
            }

            .budget-actions s25-ng-button {
                width: 100%;
            }

            .section-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }

            .section-header s25-ng-button {
                width: 100%;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CurrencyPipe,
        DecimalPipe,
        S25LoadingInlineModule,
        S25ButtonComponent,
        S25TableModule,
        BudgetEditModalComponent,
    ],
})
export class OrgBudgetComponent implements OnInit {
    readonly labels = BudgetLabels;
    readonly labelPhrases = BudgetLabelPhrases;

    readonly itemId = input.required<number>();
    readonly budgetSelected = output<number>();

    private orgSecStore = inject(SeOrgSecurityStore);
    private seSecurityStore = inject(SeSecurityStore);
    readonly canEditBudget = computed(
        () => this.orgSecStore.canManageBudgetPlan() || this.seSecurityStore.hasFLSOverride(),
    );
    readonly canViewBudget = computed(() => this.orgSecStore.canViewBudget());

    init = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    exporting = signal<boolean>(false);
    loadError = signal<string | null>(null);
    error = computed(() => {
        if (this.seSecurityStore.loadedOrgs().has(this.itemId()) && !this.orgSecStore.canViewBudget()) {
            return BudgetLabelPhrases.noPermissionViewAllocations;
        }
        return this.loadError();
    });

    orgDetails: any;
    budgetSummary: Budget.BudgetSummary | null = null;
    budgetTableData: Table.DataSource;
    budgets: Budget.OrgBudget[] = [];
    workflowNameById = new Map<number, string>();
    budgetEditModal?: BudgetEditModalComponent;

    private destroy$ = new Subject<void>();

    @ViewChild("budgetEditModal") set budgetEditModalRef(modal: BudgetEditModalComponent | undefined) {
        this.budgetEditModal = modal;
    }

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.initializeComponent();
    }

    @Bind
    private async initializeComponent(): Promise<void> {
        // Cancel any ongoing requests
        this.destroy$.next();
        this.orgSecStore.setOrgId(this.itemId());

        // Reset component state
        this.init.set(false);
        this.isLoading.set(true);
        this.loadError.set(null);

        this.orgDetails = {};
        this.budgetSummary = null;
        this.budgets = [];

        this.cd.detectChanges();

        if (this.itemId()) {
            const [resp, error] = await S25Util.Maybe(this.loadBudgetData());
            this.init.set(true);
        }
        this.loadingDone();
    }

    @Bind
    async loadBudgetData(): Promise<void> {
        this.isLoading.set(true);
        this.loadError.set(null);
        this.cd.detectChanges();

        try {
            // Load organization details
            const [orgData, orgError] = await S25Util.Maybe(OrganizationService.getOrganizationById(this.itemId()));
            if (orgError) {
                throw new Error(`Failed to load organization: ${S25Util.errorText(orgError)}`);
            }
            this.orgDetails = orgData;

            // Load budget summary
            const [budgetData, budgetError] = await S25Util.Maybe(BudgetService.getBudgetSummary(this.itemId()));
            if (budgetError) {
                throw new Error(`Failed to load budget summary: ${S25Util.errorText(budgetError)}`);
            }
            this.budgetSummary = budgetData;

            // Load all budgets for the table
            const [budgetsData, budgetsError] = await S25Util.Maybe(BudgetService.getAllOrgBudgets(this.itemId()));
            if (budgetsError) {
                this.budgets = [];
            } else {
                this.budgets = budgetsData;
                await this.loadWorkflowNames();
                this.budgetTableData = this.createBudgetTableDataSource();
            }
        } catch (error) {
            this.loadError.set(error instanceof Error ? error.message : BudgetLabelPhrases.failedLoadBudgetData);
        } finally {
            this.loadingDone();
        }
    }

    createBudgetTableDataSource(): Table.DataSource {
        return {
            type: "unpaginated",
            dataSource: this.getBudgetTableRows,
            columns: [
                {
                    id: "fiscalYear",
                    header: "Fiscal Year",
                    minWidth: 120,
                    cells: {
                        text: "",
                        className: "font-weight-medium",
                    },
                },
                {
                    id: "name",
                    header: BudgetLabels.ALLOCATION_NAME,
                    minWidth: 200,
                    cells: {
                        text: "",
                        className: "font-weight-medium",
                    },
                },
                {
                    id: "status",
                    header: "Status",
                    minWidth: 100,
                    align: "center",
                    cells: {
                        text: "",
                        className: "budget-status",
                    },
                },
                {
                    id: "totalAllocated",
                    header: "Total Allocated",
                    minWidth: 150,
                    align: "right",
                    cells: {
                        text: "",
                        className: "currency-amount",
                    },
                },
                {
                    id: "utilizationPercentage",
                    header: "Utilization",
                    minWidth: 100,
                    align: "center",
                    cells: {
                        text: "",
                        className: "utilization-percentage",
                    },
                },
                {
                    id: "workflow",
                    header: "Workflow",
                    minWidth: 140,
                    cells: {
                        text: "",
                    },
                },
                {
                    id: "budgetPlansCount",
                    header: BudgetLabels.BUDGETS,
                    minWidth: 120,
                    align: "center",
                    cells: {
                        text: "",
                        className: "plans-count",
                    },
                },
                {
                    id: "actions",
                    header: "Actions",
                    width: 140,
                    sortable: false,
                },
            ],
        };
    }

    @Bind
    async getBudgetTableRows(): Promise<Table.DataSourceResponse> {
        const rows: Table.Row[] = this.budgets.map((budget) => {
            const rowActions: TableAction[] = [
                {
                    label: "View Plans",
                    type: "outline",
                    onClick: () => this.openPlansForBudget(budget.budgetId),
                },
            ];

            if (this.canEditBudget()) {
                rowActions.push({
                    label: "Edit",
                    type: "outline",
                    onClick: () => this.editBudget(budget),
                });
            }

            return {
                id: budget.budgetId,
                name: budget.name,
                data: budget,
                cells: {
                    fiscalYear: { text: budget.fiscalYear?.name || budget.fiscalYearName || "N/A" },
                    name: { text: budget.name },
                    status: {
                        text: this.getBudgetStatus(budget),
                        className: `budget-status ${this.getBudgetStatusClass(budget)}`,
                    },
                    totalAllocated: {
                        text: S25PricingUtil.formatCurrency(budget.totalAllocated, budget.currency || "USD"),
                    },
                    utilizationPercentage: {
                        text: `${Budget.getUtilization(budget).toFixed(1)}%`,
                    },
                    workflow: { text: this.getWorkflowLabel(budget) },
                    budgetPlansCount: {
                        text: budget.budgetPlans?.length?.toString() || "0",
                    },
                    actions: {
                        component: GenericTableActionsComponent,
                        inputs: {
                            actions: rowActions,
                        },
                    },
                },
            };
        });

        return {
            rows,
            totalRows: rows.length,
        };
    }

    openPlansForBudget(budgetId: number): void {
        this.budgetSelected.emit(budgetId);
    }

    @Bind
    async exportBudgetCsv(): Promise<void> {
        if (this.exporting() || !this.orgSecStore.canViewBudget()) {
            return;
        }
        this.exporting.set(true);
        this.cd.detectChanges();
        try {
            const [exportPayload, exportError] = await S25Util.Maybe(BudgetService.getOrgBudgetExport(this.itemId()));
            if (exportError) {
                AlertService.alert(`Export failed: ${S25Util.errorText(exportError)}`);
                return;
            }
            const rows = BudgetSpreadsheetUtil.flattenExport(exportPayload);
            const organizationName =
                exportPayload.organizationName ?? this.orgDetails?.acctName ?? `org-${this.itemId()}`;
            await BudgetSpreadsheetUtil.downloadExport(
                rows,
                BudgetSpreadsheetUtil.buildExportFileName(organizationName),
            );
        } catch (caughtError) {
            AlertService.alert(`Export failed: ${S25Util.errorText(caughtError)}`);
        } finally {
            this.exporting.set(false);
            this.cd.detectChanges();
        }
    }

    editBudget(budget: Budget.OrgBudget): void {
        void this.budgetEditModal?.openEditModal(this.itemId(), budget);
    }

    async onBudgetUpdated(updatedBudget: Budget.OrgBudget): Promise<void> {
        this.budgets = this.budgets.map((budget) =>
            budget.budgetId === updatedBudget.budgetId ? { ...budget, ...updatedBudget } : budget,
        );
        await this.loadBudgetData();
    }

    private async loadWorkflowNames(): Promise<void> {
        try {
            const workflows = await WorkflowService.getWorkflowProcesses(true);
            this.workflowNameById = new Map(
                workflows.map((workflow) => [workflow.workflowProcessId!, workflow.workflowProcessName]),
            );
        } catch (error) {
            console.error("Error loading workflow names:", error);
            this.workflowNameById = new Map();
        }
    }

    getWorkflowLabel(budget: Budget.OrgBudget): string {
        if (!budget.workflowProcessId) {
            return "Auto-approve";
        }
        return this.workflowNameById.get(budget.workflowProcessId) ?? `Workflow #${budget.workflowProcessId}`;
    }

    getBudgetStatus(budget: Budget.OrgBudget): string {
        const now = new Date();
        const startDate = new Date(budget.startDate);
        const endDate = new Date(budget.endDate);

        if (now < startDate) {
            return "Upcoming";
        } else if (now > endDate) {
            return "Expired";
        } else {
            const utilization = Budget.getUtilization(budget);
            if (utilization >= 0.9) {
                return "Critical";
            } else if (utilization >= 0.75) {
                return "Warning";
            } else {
                return "Active";
            }
        }
    }

    getBudgetStatusClass(budget: Budget.OrgBudget): string {
        const status = this.getBudgetStatus(budget);
        switch (status) {
            case "Active":
                return "status-active";
            case "Warning":
                return "status-warning";
            case "Critical":
                return "status-critical";
            case "Expired":
                return "status-expired";
            case "Upcoming":
                return "status-upcoming";
            default:
                return "status-unknown";
        }
    }

    loadingDone(): void {
        this.isLoading.set(false);
        this.cd.detectChanges();
    }
}
